/**
 * nameFormatter.js — Frontend
 *
 * Centralized utility for formatting employee/user names consistently across the application.
 * Converts raw names to Title Case (e.g., "john smith" → "John Smith").
 *
 * Used by:
 *   - DashboardFilters (dropdown options for Project Owner, Project, Assignee)
 *   - MilestoneCardView (milestone creator and owner names)
 *   - TaskListTable (task assignee names)
 *   - TaskDrawerContext (drawer metadata like assignee, milestone owner)
 *   - TaskFooter (task metadata display)
 *   - TaskMetadata (task assignee, collaborators)
 *   - TaskDescription (linked employees/attachments)
 *   - CreateProjectModal (project owner dropdown)
 *   - MyTasksWorkspace (task assignee names)
 *   - AIPriorityLeaderboard (leaderboard user names)
 *   - WorkloadChart (chart label names)
 */

/**
 * Converts a name string to Title Case (proper capitalization).
 * Example: "john smith" → "John Smith"
 *
 * @param {string} name - The raw name string to format.
 * @returns {string} The formatted name in Title Case, or empty string if input is falsy.
 */
export function formatName(name) {
    if (!name || typeof name !== 'string') return '';

    return name
        .trim()
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Formats an array of names and returns a deduplicated, sorted array.
 * Useful for processing dropdown options, lists of collaborators, etc.
 *
 * @param {string[]} names - Array of raw name strings.
 * @returns {string[]} Array of formatted names, deduplicated and sorted alphabetically.
 */
export function formatNames(names) {
    if (!Array.isArray(names)) return [];

    const formatted = names
        .filter(name => name && typeof name === 'string')
        .map(name => formatName(name));

    // Remove duplicates and sort alphabetically
    return [...new Set(formatted)].sort();
}

/**
 * Formats a name object with firstName and lastName properties.
 * Example: { firstName: "john", lastName: "smith" } → "John Smith"
 *
 * @param {Object} nameObj - Object with firstName and/or lastName properties.
 * @returns {string} The formatted full name, or empty string if no valid properties.
 */
export function formatNameObject(nameObj) {
    if (!nameObj || typeof nameObj !== 'object') return '';

    const first = nameObj.firstName ? nameObj.firstName.trim() : '';
    const last = nameObj.lastName ? nameObj.lastName.trim() : '';
    const combined = [first, last].filter(Boolean).join(' ');

    return formatName(combined);
}

export default formatName;
